import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Container, Typography, IconButton, Tooltip, Box, Chip, MenuItem, Modal, Paper,
  TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Avatar, InputAdornment, CircularProgress, Divider, Grid as MuiGrid, Card, CardContent,
  Pagination, FormControl, InputLabel, OutlinedInput
} from "@mui/material";
import { useReactTable, getCoreRowModel, getPaginationRowModel, flexRender } from "@tanstack/react-table";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import EventIcon from '@mui/icons-material/Event';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';

import PaymentScheduleModal from './PaymentScheduleModal';

import { deleteQueries, getAllQueries, updateQueries, addQueryRemark, updateQueryRemark, deleteQueryRemark } from "../../api/queriesAPI";
import { getSinglePackages } from "../../api/packageAPI";
import { getAllLocations } from "../../api/locationAPI";
import usePermissions from "../../hooks/UsePermissions";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedquerie } from "../../reduxcomponents/slices/queriesSlice";
import { setSelectedInquiry } from "../../reduxcomponents/slices/inquirySlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSnackbar from "../../hooks/useSnackbar";
import { getAllUsers } from "../../api/userAPI";
import { getChangeRequest, getRejectedChanges, handleChangeRequestApproval, getSingleOperation, getQueriesByoperation } from "../../api/operationAPI";
import { exportQueryViewPDF, mapOperationToPdfData, mapOperationToInquiry } from "../createItinerary/createInquiryCalculation";

const getStatusStyles = (status) => {
  switch (status) {
    case "Confirm": return { color: "#16a34a", bg: "linear-gradient(135deg,#dcfce7,#bbf7d0)", glow: "none", icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> };
    case "Cancel": return { color: "#dc2626", bg: "linear-gradient(135deg,#fee2e2,#fecaca)", glow: "none", icon: <ErrorOutlineIcon sx={{ fontSize: 13 }} /> };
    case "FollowUp": return { color: "#d97706", bg: "linear-gradient(135deg,#fef3c7,#fde68a)", glow: "none", icon: <PendingActionsIcon sx={{ fontSize: 13 }} /> };
    case "Postponed": return { color: "#0284c7", bg: "linear-gradient(135deg,#e0f2fe,#bae6fd)", glow: "none", icon: <EventIcon sx={{ fontSize: 13 }} /> };
    case "Higher Priority": return { color: "#9333ea", bg: "linear-gradient(135deg,#f3e8ff,#e9d5ff)", glow: "none", icon: <TrendingUpIcon sx={{ fontSize: 13 }} /> };
    default: return { color: "#475569", bg: "linear-gradient(135deg,#f1f5f9,#e2e8f0)", glow: "none", icon: null };
  }
};

const ModernStatusChip = ({ status }) => {
  const styles = getStatusStyles(status);
  return (
    <Chip
      label={status}
      size="small"
      icon={styles.icon}
      sx={{
        height: 24,
        fontSize: '0.7rem',
        fontWeight: 700,
        color: styles.color,
        background: styles.bg,
        border: `1px solid ${styles.color}30`,
        '& .MuiChip-icon': { color: 'inherit' },
        borderRadius: '4px',
        letterSpacing: '0.02em'
      }}
    />
  );
};

const StatCard = ({ label, value, color, gradient, icon, onClick, active }) => (
  <Box
    onClick={onClick}
    sx={{
      minWidth: { xs: 95, sm: 110 },
      borderRadius: '4px',
      background: gradient,
      p: '8px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: 1.2,
      boxShadow: active ? `0 0 0 2px #fff, 0 4px 16px ${color}55` : `0 2px 10px rgba(0,0,0,0.08)`,
      border: active ? `2px solid #fff` : `1px solid rgba(255,255,255,0.25)`,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      transform: active ? 'translateY(-2px)' : 'none',
      userSelect: 'none',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 6px 20px ${color}44`,
        borderColor: 'rgba(255,255,255,0.6)'
      }
    }}
  >
    <Box sx={{
      width: 32, height: 32, borderRadius: '4px',
      bgcolor: `${color}25`, display: 'flex',
      alignItems: 'center', justifyContent: 'center', color
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
      <Typography sx={{ fontSize: '0.66rem', color: `${color}ee`, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>{label}</Typography>
    </Box>
  </Box>
);

const Query = () => {
  const checkPermission = usePermissions();
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.tokens?.user?.permission);
  const userId = useSelector((state) => state.tokens?.user?._id);
  const navigate = useNavigate();
  const canView = checkPermission("queries", "view");
  // const canEdit = checkPermission("queries", "alter");
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // const [query, setQuery] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stageCounts, setStageCounts] = useState({});
  const [overviewTotal, setOverviewTotal] = useState(0);
  const [extraCounts, setExtraCounts] = useState({ unassigned: 0, myLeads: 0 });

  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [quotationNoQuery, setQuotationNoQuery] = useState(searchParams.get("quotationNo") || "");
  const [statusQuery, setStatusQuery] = useState(searchParams.get("stage") || "");
  const [locationQuery, setLocationQuery] = useState(searchParams.get("location") || "");
  const [locationsList, setLocationsList] = useState(["Darjeeling", "Sikkim", "Sandakphu", "Dooars", "Kalimpong", "Gangtok", "North Sikkim", "South Sikkim"]);

  useEffect(() => {
    getAllLocations({ isActive: true })
      .then(res => {
        if (Array.isArray(res?.data) && res.data.length > 0) {
          setLocationsList(res.data.map(l => (typeof l === 'string' ? l : l.name)).filter(Boolean));
        }
      })
      .catch(() => {});
  }, []);
  const [assignedUserQuery, setAssignedUserQuery] = useState(searchParams.get("assignedUser") || "");
  const [confirmedUserQuery, setConfirmedUserQuery] = useState(searchParams.get("confirmedUser") || searchParams.get("confirmed_by") || "");
  const [travelStartDate, setTravelStartDate] = useState(searchParams.get("travelStartDate") || searchParams.get("date") || "");
  const [travelEndDate, setTravelEndDate] = useState(searchParams.get("travelEndDate") || "");
  const [createdStartDate, setCreatedStartDate] = useState(searchParams.get("createdStartDate") || "");
  const [createdEndDate, setCreatedEndDate] = useState(searchParams.get("createdEndDate") || "");
  const [sortField, setSortField] = useState(searchParams.get("sortField") || "");
  const [sortOrder, setSortOrder] = useState(searchParams.get("sortOrder") || "");
  const [page, setPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get("pageSize") || searchParams.get("limit") || searchParams.get("size")) || 25);
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [selectedQuickEditId, setSelectedQuickEditId] = useState(null);
  const [isQuickEditLocked, setIsQuickEditLocked] = useState(false);
  const [editedRowData, setEditedRowData] = useState({});
  const [pdfLoading, setPdfLoading] = useState(null); // id of current loading pdf

  // Sync state when URL params change externally (e.g., from TopSearch navigation or browser back/forward)
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    if (urlSearch !== searchQuery) setSearchQuery(urlSearch);

    const urlQuotationNo = searchParams.get("quotationNo") || "";
    if (urlQuotationNo !== quotationNoQuery) setQuotationNoQuery(urlQuotationNo);

    const urlStage = searchParams.get("stage") || "";
    if (urlStage !== statusQuery) setStatusQuery(urlStage);

    const urlLocation = searchParams.get("location") || "";
    if (urlLocation !== locationQuery) setLocationQuery(urlLocation);

    const urlAssigned = searchParams.get("assignedUser") || "";
    if (urlAssigned !== assignedUserQuery) setAssignedUserQuery(urlAssigned);

    const urlConfirmed = searchParams.get("confirmedUser") || searchParams.get("confirmed_by") || "";
    if (urlConfirmed !== confirmedUserQuery) setConfirmedUserQuery(urlConfirmed);

    const urlTravelStart = searchParams.get("travelStartDate") || searchParams.get("date") || "";
    if (urlTravelStart !== travelStartDate) setTravelStartDate(urlTravelStart);

    const urlTravelEnd = searchParams.get("travelEndDate") || "";
    if (urlTravelEnd !== travelEndDate) setTravelEndDate(urlTravelEnd);

    const urlCreatedStart = searchParams.get("createdStartDate") || "";
    if (urlCreatedStart !== createdStartDate) setCreatedStartDate(urlCreatedStart);

    const urlCreatedEnd = searchParams.get("createdEndDate") || "";
    if (urlCreatedEnd !== createdEndDate) setCreatedEndDate(urlCreatedEnd);

    const urlPage = parseInt(searchParams.get("page")) || 1;
    if (urlPage !== page) setPage(urlPage);

    const urlPageSize = parseInt(searchParams.get("pageSize") || searchParams.get("limit") || searchParams.get("size")) || 25;
    if (urlPageSize !== pageSize) setPageSize(urlPageSize);

    const urlSortField = searchParams.get("sortField") || "";
    if (urlSortField !== sortField) setSortField(urlSortField);

    const urlSortOrder = searchParams.get("sortOrder") || "";
    if (urlSortOrder !== sortOrder) setSortOrder(urlSortOrder);
  }, [searchParams]);

  // Modal and team management
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedQueryId, setSelectedQueryId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);

  // modal change request
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [selectedChangeRequests, setSelectedChangeRequests] = useState([]);
  const [currentQueryId, setCurrentQueryId] = useState(null);

  // rejected modal
  const [rejectedModalOpen, setRejectedModalOpen] = useState(false);
  const [rejectedChanges, setRejectedChanges] = useState([]);

  // delete 
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [queryToDelete, setQueryToDelete] = useState(null);

  // remarks
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [selectedQueryForRemarks, setSelectedQueryForRemarks] = useState(null);
  const [newRemarkText, setNewRemarkText] = useState("");
  const [remarkLoading, setRemarkLoading] = useState(false);

  // Payment Schedule
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedQueryForSchedule, setSelectedQueryForSchedule] = useState(null);

  const handleAddRemark = async () => {
    if (!newRemarkText.trim() || !selectedQueryForRemarks) return;
    setRemarkLoading(true);
    try {
      const res = await addQueryRemark(selectedQueryForRemarks._id, { text: newRemarkText });
      if (res.success) {
        showSnackbar("Remark added successfully", "success");
        setNewRemarkText("");
        // Update local state to reflect new remark
        const newRemark = res.data;
        setSelectedQueryForRemarks(prev => ({
          ...prev,
          remarks: [...(prev.remarks || []), newRemark]
        }));
        // Update query in main list
        setQueries(prevQueries => prevQueries.map(q => 
          q._id === selectedQueryForRemarks._id 
            ? { ...q, remarks: [...(q.remarks || []), newRemark] }
            : q
        ));
      }
    } catch (error) {
      console.error("Failed to add remark:", error);
      showSnackbar("Failed to add remark", "error");
    } finally {
      setRemarkLoading(false);
    }
  };

  const [editingRemarkId, setEditingRemarkId] = useState(null);
  const [editRemarkText, setEditRemarkText] = useState("");

  const handleEditRemarkSave = async (remarkId) => {
    if (!editRemarkText.trim() || !selectedQueryForRemarks) return;
    setRemarkLoading(true);
    try {
      const res = await updateQueryRemark(selectedQueryForRemarks._id, remarkId, { text: editRemarkText });
      if (res.success) {
        showSnackbar("Remark updated successfully", "success");
        setEditingRemarkId(null);
        setEditRemarkText("");
        
        const updatedRemark = res.data;
        setSelectedQueryForRemarks(prev => ({
          ...prev,
          remarks: prev.remarks.map(r => r._id === remarkId ? updatedRemark : r)
        }));
        
        setQueries(prevQueries => prevQueries.map(q => 
          q._id === selectedQueryForRemarks._id 
            ? { ...q, remarks: q.remarks.map(r => r._id === remarkId ? updatedRemark : r) }
            : q
        ));
      }
    } catch (error) {
      console.error("Failed to update remark:", error);
      showSnackbar(error.response?.data?.message || "Failed to update remark", "error");
    } finally {
      setRemarkLoading(false);
    }
  };

  const handleDeleteRemark = async (remarkId) => {
    if (!window.confirm("Are you sure you want to delete this remark?")) return;
    setRemarkLoading(true);
    try {
      const res = await deleteQueryRemark(selectedQueryForRemarks._id, remarkId);
      if (res.success) {
        showSnackbar("Remark deleted successfully", "success");
        setSelectedQueryForRemarks(prev => ({
          ...prev,
          remarks: prev.remarks.filter(r => r._id !== remarkId)
        }));
        
        setQueries(prevQueries => prevQueries.map(q => 
          q._id === selectedQueryForRemarks._id 
            ? { ...q, remarks: q.remarks.filter(r => r._id !== remarkId) }
            : q
        ));
      }
    } catch (error) {
      console.error("Failed to delete remark:", error);
      showSnackbar(error.response?.data?.message || "Failed to delete remark", "error");
    } finally {
      setRemarkLoading(false);
    }
  };

  const fetchQuery = useCallback(async (pageNum = page, sizeNum = pageSize, currentFilters = null) => {
    setLoading(true);
    try {
      const filters = currentFilters || {
        search: searchQuery,
        quotationNo: quotationNoQuery,
        stage: statusQuery,
        location: locationQuery,
        assignedUser: assignedUserQuery,
        confirmedUser: confirmedUserQuery,
        travelStartDate,
        travelEndDate,
        createdStartDate,
        createdEndDate,
        sortField,
        sortOrder
      };
      const cleanFilters = {};
      Object.keys(filters).forEach(k => {
        if (filters[k]) cleanFilters[k] = filters[k];
      });

      const response = await getAllQueries(pageNum, sizeNum, cleanFilters);
      let newData = response.data || [];
      
      if (userRole === "Sales Executive" && userId) {
        newData = newData.filter(q => {
          const assignedIds = q.manage_teams?.map(mt => (mt.user_id && typeof mt.user_id === 'object') ? (mt.user_id._id || mt.user_id.id) : mt.user_id) || [];
          return assignedIds.includes(userId);
        });
      }

      setQueries(newData);
      const total = response.pagination?.totalItems ?? response.totalRows ?? newData.length;
      setTotalCount(total);
      setTotalPages(response.pagination?.totalPages || Math.ceil(total / sizeNum) || 1);
      if (response.stageCounts) {
        setStageCounts(response.stageCounts);
      }
      if (response.overviewTotal !== undefined) {
        setOverviewTotal(response.overviewTotal);
      }
      setExtraCounts({
        unassigned: response.unassignedCount ?? 0,
        myLeads: response.myLeadsCount ?? 0
      });
    } catch (error) {
      console.error("Error fetching query:", error);
    } finally {
      setLoading(false);
    }
  }, [
    userRole, userId, page, pageSize, searchQuery, quotationNoQuery, statusQuery,
    locationQuery, assignedUserQuery, confirmedUserQuery, travelStartDate, travelEndDate,
    createdStartDate, createdEndDate, sortField, sortOrder
  ]);

  // Sync state to URL and fetch on filter or page change
  useEffect(() => {
    const filters = {};
    if (searchQuery) filters.search = searchQuery;
    if (quotationNoQuery) filters.quotationNo = quotationNoQuery;
    if (statusQuery) filters.stage = statusQuery;
    if (locationQuery) filters.location = locationQuery;
    if (assignedUserQuery) filters.assignedUser = assignedUserQuery;
    if (confirmedUserQuery) filters.confirmedUser = confirmedUserQuery;
    if (travelStartDate) filters.travelStartDate = travelStartDate;
    if (travelEndDate) filters.travelEndDate = travelEndDate;
    if (createdStartDate) filters.createdStartDate = createdStartDate;
    if (createdEndDate) filters.createdEndDate = createdEndDate;
    if (sortField) filters.sortField = sortField;
    if (sortOrder) filters.sortOrder = sortOrder;
    if (page > 1) filters.page = page;
    if (pageSize !== 25) filters.pageSize = pageSize;

    // Update URL query parameters
    const params = new URLSearchParams(filters);
    setSearchParams(params, { replace: true });

    if (canView) {
      fetchQuery(page, pageSize, filters);
    }
  }, [
    searchQuery, quotationNoQuery, statusQuery, locationQuery, assignedUserQuery, confirmedUserQuery,
    travelStartDate, travelEndDate, createdStartDate, createdEndDate,
    sortField, sortOrder, page, pageSize, canView
  ]);

  const fetchUsers = async () => {
    try {
      const users = await getAllUsers();
      setAllUsers((users.data || []).filter(u => u.isActive !== false));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSaveEdit = useCallback(async (id, dataToSave) => {
    try {
      const updatedFields = {};
      if (dataToSave.guest_name !== undefined) updatedFields.guest_name = dataToSave.guest_name;
      if (dataToSave.guest_email !== undefined) updatedFields.guest_email = dataToSave.guest_email;
      if (dataToSave.guest_phone !== undefined) updatedFields.guest_phone = dataToSave.guest_phone;
      if (dataToSave.guest_country_code !== undefined) updatedFields.guest_country_code = dataToSave.guest_country_code;
      if (dataToSave.guest_address !== undefined) updatedFields.guest_address = dataToSave.guest_address;
      if (dataToSave.cost !== undefined) updatedFields.cost = dataToSave.cost;
      if (dataToSave.advance !== undefined) updatedFields.advance = dataToSave.advance;
      if (dataToSave.lead_stage !== undefined) {
        updatedFields.lead_stage = dataToSave.lead_stage;
        if (dataToSave.lead_stage === "Confirm") {
          updatedFields.confirm_date = new Date().toISOString();
        }
      }

      const response = await updateQueries(id, updatedFields);
      if (response.success) showSnackbar(response.message, "success");

      fetchQuery(page, pageSize);
      setQuickEditOpen(false);
      setSelectedQuickEditId(null);
      setEditedRowData({});
    } catch (error) {
      console.error("Update failed:", error);
      showSnackbar("Failed to update lead", "error");
    }
  }, [fetchQuery, page, pageSize, showSnackbar]);

  const stats = useMemo(() => {
    let confirm = 0;
    let followUp = 0;
    let pending = 0;
    let priority = 0;
    let postponed = 0;
    let cancel = 0;

    if (stageCounts && Object.keys(stageCounts).length > 0) {
      confirm = stageCounts["Confirm"] || 0;
      followUp = stageCounts["FollowUp"] || 0;
      pending = (stageCounts["Pending"] || 0) + (stageCounts["New"] || 0) + (stageCounts["new"] || 0) + (stageCounts["pending"] || 0);
      priority = (stageCounts["Higher Priority"] || 0) + (stageCounts["Priority"] || 0);
      postponed = stageCounts["Postponed"] || 0;
      cancel = (stageCounts["Cancel"] || 0) + (stageCounts["Cancelled"] || 0);
    } else {
      queries.forEach(q => {
        if (q.lead_stage === "Confirm") confirm++;
        else if (q.lead_stage === "FollowUp") followUp++;
        else if (q.lead_stage === "Higher Priority" || q.lead_stage === "Priority") priority++;
        else if (q.lead_stage === "Postponed") postponed++;
        else if (q.lead_stage === "Cancel" || q.lead_stage === "Cancelled") cancel++;
        else pending++;
      });
    }

    const total = overviewTotal || totalCount || (confirm + followUp + pending + priority + postponed + cancel);

    return {
      total,
      confirm,
      followUp,
      pending,
      priority,
      postponed,
      cancel,
      unassigned: extraCounts.unassigned,
      myLeads: extraCounts.myLeads
    };
  }, [stageCounts, totalCount, overviewTotal, extraCounts, queries]);

  const handleEditOpen = useCallback((rowData) => {
    dispatch(setSelectedquerie({ ...rowData, id: rowData.operation_id }));
    navigate(`/query/view/${rowData.operation_id || rowData._id}`);
  }, [dispatch, navigate]);
  const handleOpenDeleteDialog = useCallback((row) => {
    setQueryToDelete(row);
    setDeleteDialogOpen(true);
  }, []);


  const openUserModal = useCallback((row) => {
    setSelectedQueryId(row._id);
    const assigned = row.manage_teams?.map(mt => {
      return (mt.user_id && typeof mt.user_id === 'object') ? (mt.user_id._id || mt.user_id.id) : mt.user_id;
    }) || [];
    setAssignedUsers(assigned);
    setUserModalOpen(true);
  }, []);

  const saveUserAssignments = async () => {
    try {
      const manage_teams = assignedUsers.map(userId => ({
        user_id: userId,
        date: new Date(),
        status: "Assigned",
      }));
      const response = await updateQueries(selectedQueryId, { manage_teams });
      if (response.success) {
        showSnackbar("Users assigned successfully", "success");
        fetchQuery();
      } else {
        showSnackbar("Assignment failed", "error");
      }
      setUserModalOpen(false);
    } catch (err) {
      console.error("Assignment failed", err);
      showSnackbar("Something went wrong", "error");
    }
  };

  const openChangeRequestModal = useCallback(async (queryId) => {
    try {
      const res = await getChangeRequest(queryId);
      setSelectedChangeRequests(res);
      setCurrentQueryId(queryId);
      setChangeModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch change requests", err);
      showSnackbar("Failed to load change requests", "error");
    }
  }, [showSnackbar]);
  const handleChangeRequestAction = async (changeId, action, reason = "") => {
    try {
      const body = { status: action };
      if (action === "Rejected") body.reason = reason;
      const res = await handleChangeRequestApproval(currentQueryId, changeId, body)

      if (!res) throw new Error("Action failed");

      showSnackbar(`Change ${action.toLowerCase()} successfully`, "success");
      openChangeRequestModal(currentQueryId); // Refresh modal list
      fetchQuery(1); // Refresh main list to update counts
    } catch (err) {
      console.error(err);
      showSnackbar("Action failed", "error");
    }
  };
  const openRejectedChangeModal = useCallback(async (operationId) => {
    try {
      const res = await getRejectedChanges(operationId);
      setRejectedChanges(res);
      setRejectedModalOpen(true);
    } catch (err) {
      console.error("Failed to load rejected changes", err);
      showSnackbar("Failed to load rejected changes", "error");
    }
  }, [showSnackbar]);
  const handleDeleteQuery = async () => {
    try {
      const res = await deleteQueries(queryToDelete._id);
      if (res.success) {
        showSnackbar("Query deleted successfully", "success");
        setQueries(prev => prev.filter(q => q._id !== queryToDelete._id));
      } else {
        showSnackbar("Failed to delete query", "error");
      }
    } catch (err) {
      console.error("Delete failed:", err);
      showSnackbar("Something went wrong while deleting", "error");
    } finally {
      setDeleteDialogOpen(false);
      setQueryToDelete(null);
    }
  };

  const handleDownloadPDF = async (rowData) => {
    setPdfLoading(rowData._id);
    try {
      const operationData = await getSingleOperation(rowData.operation_id || rowData._id);
      const queriesData = await getQueriesByoperation(operationData._id);
      const { guestInfo, itineraryData } = mapOperationToPdfData(operationData, queriesData);
      if (!guestInfo.quotation_no) {
        guestInfo.quotation_no = rowData.quotation_no || rowData.quotationNo || '';
      }
      await exportQueryViewPDF({ guestInfo, itineraryData }, { isPreview: false }, (loading) => {
        if (!loading) setPdfLoading(null);
      }, showSnackbar);
    } catch (error) {
      console.error("PDF generation failed:", error);
      showSnackbar("Failed to generate PDF", "error");
      setPdfLoading(null);
    }
  };

  const handleFullEdit = async (rowData) => {
    setLoading(true);
    try {
      // Fetch full operation and query data to ensure we have the itinerary
      const opId = rowData.operation_id || rowData._id;
      const [operationData, queriesData] = await Promise.all([
        getSingleOperation(opId),
        getQueriesByoperation(opId)
      ]);

      // Handle queriesData if it's an array or nested under .data
      const rawQueries = queriesData?.data || queriesData;
      let queryObj = Array.isArray(rawQueries) ? (rawQueries.find(q => q._id === rowData._id) || rawQueries[0]) : rawQueries;

      let packageData = null;
      const pkgId = queryObj?.package_id?._id || queryObj?.package_id;
      if (pkgId) {
        try {
          const pkgRes = await getSinglePackages(pkgId);
          packageData = pkgRes.data?.data || pkgRes.data;
        } catch (err) {
          console.warn("Failed to fetch package details, continuing without it", err);
        }
      }

      const mappedData = mapOperationToInquiry(operationData, queryObj, packageData);
      console.log("Mapped Data for Full Edit:", mappedData);
      
      if (mappedData) {
        dispatch(setSelectedInquiry(mappedData));
        navigate("/createItinerary");
      } else {
        // Fallback to basic mapping if API fails
        const fallbackData = {
          ...rowData,
          _id: rowData._id,
          quotation_no: rowData.quotation_no || rowData.quotationNo || '',
          guest_name: rowData.guest_info?.guest_name,
          guest_email: rowData.guest_info?.guest_email,
          guest_phone: rowData.guest_info?.guest_phone,
          guest_country_code: rowData.guest_info?.guest_country_code,
        };
        dispatch(setSelectedInquiry(fallbackData));
        navigate("/createItinerary");
      }
    } catch (error) {
      console.error("Full Edit failed to fetch detailed data:", error);
      showSnackbar("Failed to load full details, using basic info", "warning");
      // Fallback
      dispatch(setSelectedInquiry(rowData));
      navigate("/createItinerary");
    } finally {
      setLoading(false);
    }
  };

  // Column definitions for @tanstack/react-table
  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: "guest_info.guest_name",
        header: "Lead Name",
        cell: ({ row }) => {
          const rowData = row.original;
          return (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              onClick={() => handleEditOpen(rowData)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  '& .MuiTypography-root': { color: 'primary.main' },
                  '& .MuiAvatar-root': { transform: 'scale(1.1)' }
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ transition: 'color 0.2s' }}>
                {rowData.guest_info?.guest_name || "N/A"}
              </Typography>
            </Stack>
          );
        },
      },
      {
        accessorKey: "guest_info.guest_phone",
        header: "Contact Details",
        cell: ({ row }) => {
          const rowData = row.original;
          return (
            <Box>
              <Typography variant="body2" fontWeight={500}>{`${rowData.guest_info?.guest_country_code || "+91"} ${rowData.guest_info?.guest_phone || ""}`}</Typography>
              <Typography variant="caption" color="text.secondary">{rowData.guest_info?.guest_email || ""}</Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        cell: ({ row }) => {
          const date = row.original.created_at;
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {date ? new Date(date).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              }) : "N/A"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "travel_date",
        header: () => (
          <Stack 
            direction="row" 
            alignItems="center" 
            spacing={0.5} 
            sx={{ cursor: 'pointer', display: 'inline-flex' }}
            onClick={() => {
              const isAsc = sortField === 'travel_date' && sortOrder === 'asc';
              setSortField('travel_date');
              setSortOrder(isAsc ? 'desc' : 'asc');
            }}
          >
            <Box>Travel Date</Box>
            {sortField === 'travel_date' ? (
              sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="inherit" /> : <ArrowDownwardIcon fontSize="inherit" />
            ) : null}
          </Stack>
        ),
        cell: ({ row }) => {
          const date = row.original.travel_date;
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {date ? new Date(date).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              }) : "N/A"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "lead_stage",
        header: "Stage",
        cell: ({ row }) => {
          const rowData = row.original;
          return <ModernStatusChip status={rowData.lead_stage} />;
        },
      },
      {
        accessorKey: "cost",
        header: "Total Cost",
        cell: ({ row }) => {
          const rowData = row.original;
          return (
            <Typography variant="body2" fontWeight={700} color="primary.main">
              ₹{Number(rowData.cost || 0).toLocaleString('en-IN')}
            </Typography>
          );
        },
      },
      {
        accessorKey: "advance",
        header: "Advance",
        cell: ({ row }) => {
          const rowData = row.original;
          return (
            <Typography variant="body2" fontWeight={600} color={rowData.advance > 0 ? "success.main" : "text.secondary"}>
              ₹{Number(rowData.advance || 0).toLocaleString('en-IN')}
            </Typography>
          );
        },
      },
      {
        id: "due_cost",
        header: "Due Cost",
        cell: ({ row }) => {
          const rowData = row.original;
          const due = (Number(rowData.cost) || 0) - (Number(rowData.advance) || 0);
          return (
            <Typography variant="body2" fontWeight={600} color={due > 0 ? "error.main" : "text.secondary"}>
              ₹{due.toLocaleString('en-IN')}
            </Typography>
          );
        },
      },
      {
        id: "assigned_users",
        header: "Assigned To",
        cell: ({ row }) => {
          const manage_teams = row.original.manage_teams || [];
          if (manage_teams.length === 0) {
            return <Typography variant="caption" color="error" fontWeight="bold">Unassigned</Typography>;
          }
          const userNames = manage_teams.map(team => {
            if (team.user_id && typeof team.user_id === 'object' && team.user_id.fullName) {
              return team.user_id.fullName;
            }
            const userIdToFind = (team.user_id && typeof team.user_id === 'object') ? (team.user_id._id || team.user_id.id) : team.user_id;
            const user = allUsers.find(u => u.id === userIdToFind || u._id === userIdToFind);
            return user ? user.fullName : "Unknown";
          });
          return (
            <Typography variant="caption" color="primary.main" fontWeight="bold">
              {userNames.join(', ')}
            </Typography>
          );
        }
      },
      ...(checkPermission("user", "view") ? [
        {
          accessorKey: "created_by_name",
          header: "Created By",
          cell: ({ getValue }) => (
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              {getValue() || "-"}
            </Typography>
          )
        }
      ] : []),
      ...((checkPermission("user", "view") || ["Operation", "Operation Manager"].includes(userRole)) ? [
        {
          accessorKey: "confirmed_by_name",
          header: "Confirmed By & Date",
          cell: ({ row, getValue }) => (
            <Box>
              <Typography variant="caption" sx={{ fontStyle: 'italic', fontWeight: 'bold', color: 'success.dark', display: 'block' }}>
                {getValue() || "-"}
              </Typography>
              {row.original.confirm_date && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(row.original.confirm_date).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', hour12: true
                  })}
                </Typography>
              )}
            </Box>
          )
        }
      ] : []),
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const rowData = row.original;
          return (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Quick Edit">
                <IconButton size="small" color="primary" onClick={() => {
                  setSelectedQuickEditId(rowData._id);
                  const isLocked = (userRole === "User" || userRole === "Operation") && rowData.advance > 0;
                  setIsQuickEditLocked(isLocked);
                  setEditedRowData({
                    guest_name: rowData.guest_info?.guest_name || "",
                    guest_email: rowData.guest_info?.guest_email || "",
                    guest_phone: rowData.guest_info?.guest_phone || "",
                    guest_country_code: rowData.guest_info?.guest_country_code || rowData.guest_country_code || "+91",
                    guest_address: rowData.guest_info?.guest_address || "",
                    cost: rowData.cost || "",
                    advance: rowData.advance || "",
                    lead_stage: rowData.lead_stage || "",
                  });
                  setQuickEditOpen(true);
                }}>
                  <EditNoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {(() => {
                const isLocked = (userRole === "User" || userRole === "Operation") && rowData.advance > 0;
                return (
                  <Tooltip title={isLocked ? "Locked after advance" : "Full Edit"}>
                    <span>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleFullEdit(rowData)}
                        disabled={isLocked}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              })()}
              {
                (rowData.lead_stage?.toLowerCase() === 'confirm' || rowData.lead_stage?.toLowerCase() === 'confirmed') && (
                  <>
                    <Tooltip title="Manage Itinerary">
                      <IconButton size="small" color="info" onClick={() => handleEditOpen(rowData)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {['Admin', 'Admin-a', 'Sales Manager', 'Sales Team Lead', 'Operation Manager'].includes(userRole) && (
                      <Tooltip title="Payment Schedule">
                        <IconButton size="small" color="success" onClick={() => {
                          setSelectedQueryForSchedule(rowData);
                          setScheduleModalOpen(true);
                        }}>
                          <PaymentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )
              }
              <Tooltip title="Download PDF">
                <IconButton
                  size="small"
                  sx={{ color: '#d32f2f' }}
                  onClick={() => handleDownloadPDF(rowData)}
                  disabled={pdfLoading === rowData._id}
                >
                  {pdfLoading === rowData._id ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PictureAsPdfIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              {
                checkPermission("queries", "assuser") && (
                  <Tooltip title="Assign Users">
                    <IconButton size="small" color="secondary" onClick={() => openUserModal(rowData)}>
                      <AssignmentIndIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }
              <Tooltip title="Remarks">
                <IconButton size="small" sx={{ color: '#9c27b0' }} onClick={() => {
                  setSelectedQueryForRemarks(rowData);
                  setRemarksModalOpen(true);
                }}>
                  <ChatBubbleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack >
          );
        },
      },
    ];

    if (checkPermission("operation", "change-request")) {
      cols.push({
        id: "changeRequest",
        header: "Requests",
        cell: ({ row }) => {
          const rowData = row.original;
          return (
            <Stack direction="row" spacing={1.5}>
              <Tooltip title={`${rowData.rejected_changes || 0} Rejected Changes`}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <IconButton size="small" color="error" onClick={() => openRejectedChangeModal(rowData.operation_id || rowData._id)}>
                    <ErrorOutlineIcon fontSize="small" />
                  </IconButton>
                  {rowData.rejected_changes > 0 && (
                    <Box sx={{
                      position: 'absolute', top: -4, right: -4,
                      bgcolor: 'error.main', color: 'white',
                      borderRadius: '50%', width: 16, height: 16,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '10px', fontWeight: 'bold', border: '2px solid white'
                    }}>
                      {rowData.rejected_changes}
                    </Box>
                  )}
                </Box>
              </Tooltip>
              <Tooltip title={`${rowData.pending_changes || 0} Pending Requests`}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <IconButton size="small" color="warning" onClick={() => openChangeRequestModal(rowData.operation_id || rowData._id)}>
                    <ChangeCircleIcon fontSize="small" />
                  </IconButton>
                  {rowData.pending_changes > 0 && (
                    <Box sx={{
                      position: 'absolute', top: -4, right: -4,
                      bgcolor: 'warning.main', color: 'white',
                      borderRadius: '50%', width: 16, height: 16,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '10px', fontWeight: 'bold', border: '2px solid white'
                    }}>
                      {rowData.pending_changes}
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </Stack>
          );
        },
      });
    }

    if (checkPermission("queries", "delete")) {
      cols.push({
        id: "delete",
        header: "",
        cell: ({ row }) => (
          <Tooltip title="Delete Lead">
            <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(row.original)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      });
    }

    return cols;
  }, [checkPermission, handleEditOpen, openUserModal, openRejectedChangeModal, openChangeRequestModal, handleOpenDeleteDialog, allUsers]);

  // Table instance
  const table = useReactTable({
    data: queries,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: queries.length > 0 ? queries.length : 10,
        pageIndex: 0,
      },
    },
    manualPagination: true,
  });

  return (
    <Container maxWidth={false} sx={{ minHeight: '100vh', py: 2.5, px: { xs: 2, md: 3 }, bgcolor: '#f0f4f8', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ── Premium Header ── */}
      <Box sx={{
        borderRadius: '4px',
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #0ea5e9 100%)',
        p: { xs: 2, md: '20px 28px' },
        boxShadow: '0 8px 32px rgba(30,64,175,0.25)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -60, right: -60,
          width: 220, height: 220, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.07)'
        },
        '&::after': {
          content: '""', position: 'absolute', bottom: -40, right: 120,
          width: 130, height: 130, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.05)'
        }
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.65rem' }, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', lineHeight: 1.2 }}>
              Lead Management
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', mt: 0.4, fontWeight: 500 }}>
              Track, manage and convert your travel leads
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchQuery(page, pageSize)}
              disabled={loading}
              sx={{
                borderRadius: '4px', textTransform: 'none', fontWeight: 600,
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                backdropFilter: 'blur(8px)', bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.18)', borderColor: '#fff' }
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/createItinerary")}
              sx={{
                borderRadius: '4px', textTransform: 'none', fontWeight: 700,
                bgcolor: '#fff', color: '#1e40af',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                '&:hover': { bgcolor: '#f0f9ff', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }
              }}
            >
              New Lead
            </Button>
          </Stack>
        </Stack>
        {/* Stat Cards Row (Clickable quick access filter options) */}
        <Stack direction="row" spacing={1.2} sx={{ mt: 2.2, flexWrap: 'wrap', gap: 1.2 }}>
          <StatCard
            label="Total"
            value={stats.total}
            color="#ffffff"
            gradient="linear-gradient(135deg,rgba(255,255,255,0.22),rgba(255,255,255,0.08))"
            icon={<FilterListIcon fontSize="small" />}
            onClick={() => {
              setStatusQuery("");
              setAssignedUserQuery("");
              setConfirmedUserQuery("");
              setPage(1);
            }}
            active={!statusQuery && !assignedUserQuery && !confirmedUserQuery}
          />
          <StatCard
            label="Confirmed"
            value={stats.confirm}
            color="#4ade80"
            gradient="linear-gradient(135deg,rgba(74,222,128,0.25),rgba(74,222,128,0.08))"
            icon={<CheckCircleIcon fontSize="small" />}
            onClick={() => {
              setStatusQuery(prev => prev === "Confirm" ? "" : "Confirm");
              setAssignedUserQuery("");
              setConfirmedUserQuery("");
              setPage(1);
            }}
            active={statusQuery === "Confirm"}
          />
          <StatCard
            label="Follow Ups"
            value={stats.followUp}
            color="#fbbf24"
            gradient="linear-gradient(135deg,rgba(251,191,36,0.25),rgba(251,191,36,0.08))"
            icon={<PendingActionsIcon fontSize="small" />}
            onClick={() => {
              setStatusQuery(prev => prev === "FollowUp" ? "" : "FollowUp");
              setAssignedUserQuery("");
              setConfirmedUserQuery("");
              setPage(1);
            }}
            active={statusQuery === "FollowUp"}
          />
          <StatCard
            label="Pending"
            value={stats.pending}
            color="#e2e8f0"
            gradient="linear-gradient(135deg,rgba(226,232,240,0.25),rgba(226,232,240,0.08))"
            icon={<TrendingUpIcon fontSize="small" />}
            onClick={() => {
              setStatusQuery(prev => prev === "Pending" ? "" : "Pending");
              setAssignedUserQuery("");
              setConfirmedUserQuery("");
              setPage(1);
            }}
            active={statusQuery === "Pending"}
          />
          <StatCard
            label="Priority"
            value={stats.priority}
            color="#d8b4fe"
            gradient="linear-gradient(135deg,rgba(216,180,254,0.25),rgba(216,180,254,0.08))"
            icon={<StarIcon fontSize="small" />}
            onClick={() => {
              setStatusQuery(prev => prev === "Higher Priority" ? "" : "Higher Priority");
              setAssignedUserQuery("");
              setConfirmedUserQuery("");
              setPage(1);
            }}
            active={statusQuery === "Higher Priority"}
          />
          <StatCard
            label="Postponed"
            value={stats.postponed}
            color="#7dd3fc"
            gradient="linear-gradient(135deg,rgba(125,211,252,0.25),rgba(125,211,252,0.08))"
            icon={<EventIcon fontSize="small" />}
            onClick={() => {
              setStatusQuery(prev => prev === "Postponed" ? "" : "Postponed");
              setAssignedUserQuery("");
              setConfirmedUserQuery("");
              setPage(1);
            }}
            active={statusQuery === "Postponed"}
          />
          <StatCard
            label="Cancelled"
            value={stats.cancel}
            color="#fca5a5"
            gradient="linear-gradient(135deg,rgba(252,165,165,0.25),rgba(252,165,165,0.08))"
            icon={<CancelIcon fontSize="small" />}
            onClick={() => {
              setStatusQuery(prev => prev === "Cancel" ? "" : "Cancel");
              setAssignedUserQuery("");
              setConfirmedUserQuery("");
              setPage(1);
            }}
            active={statusQuery === "Cancel"}
          />
          <StatCard
            label="Unassigned"
            value={stats.unassigned}
            color="#fda4af"
            gradient="linear-gradient(135deg,rgba(253,164,175,0.25),rgba(253,164,175,0.08))"
            icon={<PersonOffIcon fontSize="small" />}
            onClick={() => {
              setAssignedUserQuery(prev => prev === "unassigned" ? "" : "unassigned");
              setStatusQuery("");
              setConfirmedUserQuery("");
              setPage(1);
            }}
            active={assignedUserQuery === "unassigned"}
          />
          <StatCard
            label="My Leads"
            value={stats.myLeads}
            color="#a5b4fc"
            gradient="linear-gradient(135deg,rgba(165,180,252,0.25),rgba(165,180,252,0.08))"
            icon={<PersonIcon fontSize="small" />}
            onClick={() => {
              setAssignedUserQuery(prev => prev === userId ? "" : (userId || ""));
              setStatusQuery("");
              setConfirmedUserQuery("");
              setPage(1);
            }}
            active={Boolean(userId && assignedUserQuery === userId)}
          />
        </Stack>
      </Box>

      {/* ── Filter Bar ── */}
      <Paper elevation={0} sx={{
        p: '14px 16px', borderRadius: '4px',
        border: '1px solid #e2e8f0',
        bgcolor: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
      }}>
        <Stack spacing={1.5}>
          {/* Row 1: Search, Quotation No, Stage, Location, Assigned User, Confirmed By, Reset Button */}
          <MuiGrid container spacing={1.5} alignItems="center">
            <MuiGrid item xs={12} sm={6} md={2.2}>
              <TextField
                fullWidth
                placeholder="Search name, phone, email…"
                size="small"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '4px', bgcolor: '#f8fafc',
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#94a3b8' },
                  }
                }}
              />
            </MuiGrid>
            <MuiGrid item xs={6} sm={3} md={1.4}>
              <TextField
                fullWidth
                placeholder="Quotation #"
                label="Quote No"
                size="small"
                value={quotationNoQuery}
                onChange={(e) => {
                  setQuotationNoQuery(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  sx: {
                    borderRadius: '4px', bgcolor: '#f8fafc',
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#94a3b8' },
                  }
                }}
              />
            </MuiGrid>
            <MuiGrid item xs={6} sm={3} md={1.3}>
              <TextField
                fullWidth select label="Stage" size="small"
                value={statusQuery}
                onChange={(e) => {
                  setStatusQuery(e.target.value);
                  setPage(1);
                }}
                InputProps={{ sx: { borderRadius: '4px', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } } }}
              >
                <MenuItem value="">All Stages</MenuItem>
                {["Confirm", "Cancel", "FollowUp", "Postponed", "Higher Priority", "Pending"].map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
            </MuiGrid>
            <MuiGrid item xs={6} sm={3} md={1.3}>
              <TextField
                fullWidth select label="Location" size="small"
                value={locationQuery}
                onChange={(e) => {
                  setLocationQuery(e.target.value);
                  setPage(1);
                }}
                InputProps={{ sx: { borderRadius: '4px', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } } }}
              >
                <MenuItem value="">All Locations</MenuItem>
                {locationsList.map(l => (
                  <MenuItem key={l} value={l}>{l}</MenuItem>
                ))}
              </TextField>
            </MuiGrid>
            <MuiGrid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="assigned-user-select-label">Assigned User</InputLabel>
                <Select
                  labelId="assigned-user-select-label"
                  label="Assigned User"
                  value={assignedUserQuery}
                  onChange={(e) => {
                    setAssignedUserQuery(e.target.value);
                    setPage(1);
                  }}
                  sx={{ borderRadius: '4px', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}
                >
                  <MenuItem value=""><em>All Assigned</em></MenuItem>
                  <MenuItem value="unassigned" sx={{ color: 'error.main', fontWeight: 600 }}>
                    Unassigned Leads
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                  {allUsers.map(user => (
                    <MenuItem key={user.id || user._id} value={user.id || user._id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: '#3b82f6' }}>
                          {user.fullName?.[0]}
                        </Avatar>
                        <Typography variant="body2">{user.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary">({user.permission})</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MuiGrid>
            <MuiGrid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="confirmed-user-select-label">Confirmed By</InputLabel>
                <Select
                  labelId="confirmed-user-select-label"
                  label="Confirmed By"
                  value={confirmedUserQuery}
                  onChange={(e) => {
                    setConfirmedUserQuery(e.target.value);
                    setPage(1);
                  }}
                  sx={{ borderRadius: '4px', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}
                >
                  <MenuItem value=""><em>All Confirmed</em></MenuItem>
                  <MenuItem value="unconfirmed" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Unconfirmed
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                  {allUsers.map(user => (
                    <MenuItem key={user.id || user._id} value={user.id || user._id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: '#10b981' }}>
                          {user.fullName?.[0]}
                        </Avatar>
                        <Typography variant="body2">{user.fullName}</Typography>
                        <Typography variant="caption" color="text.secondary">({user.permission})</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MuiGrid>
            <MuiGrid item xs={12} sm={4} md={1}>
              <Tooltip title="Reset All Filters">
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSearchQuery("");
                    setQuotationNoQuery("");
                    setStatusQuery("");
                    setLocationQuery("");
                    setAssignedUserQuery("");
                    setConfirmedUserQuery("");
                    setTravelStartDate("");
                    setTravelEndDate("");
                    setCreatedStartDate("");
                    setCreatedEndDate("");
                    setPage(1);
                  }}
                  disabled={!searchQuery && !quotationNoQuery && !statusQuery && !locationQuery && !assignedUserQuery && !confirmedUserQuery && !travelStartDate && !travelEndDate && !createdStartDate && !createdEndDate}
                  sx={{
                    borderRadius: '4px',
                    height: 40,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#cbd5e1',
                    color: '#64748b',
                    bgcolor: '#f8fafc',
                    '&:hover': { bgcolor: '#fee2e2', borderColor: '#ef4444', color: '#dc2626' }
                  }}
                >
                  Reset
                </Button>
              </Tooltip>
            </MuiGrid>
          </MuiGrid>

          {/* Row 2: Travel Date & Created Date Range Filters */}
          <MuiGrid container spacing={1.5} alignItems="center">
            <MuiGrid item xs={6} sm={3} md={3}>
              <TextField
                fullWidth type="date" label="Travel Date (From)" size="small"
                value={travelStartDate}
                onChange={(e) => {
                  setTravelStartDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EventIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment>,
                  sx: { borderRadius: '4px', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }
                }}
              />
            </MuiGrid>
            <MuiGrid item xs={6} sm={3} md={3}>
              <TextField
                fullWidth type="date" label="Travel Date (To)" size="small"
                value={travelEndDate}
                onChange={(e) => {
                  setTravelEndDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EventIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment>,
                  sx: { borderRadius: '4px', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }
                }}
              />
            </MuiGrid>
            <MuiGrid item xs={6} sm={3} md={3}>
              <TextField
                fullWidth type="date" label="Created Date (From)" size="small"
                value={createdStartDate}
                onChange={(e) => {
                  setCreatedStartDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EventIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment>,
                  sx: { borderRadius: '4px', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }
                }}
              />
            </MuiGrid>
            <MuiGrid item xs={6} sm={3} md={3}>
              <TextField
                fullWidth type="date" label="Created Date (To)" size="small"
                value={createdEndDate}
                onChange={(e) => {
                  setCreatedEndDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EventIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment>,
                  sx: { borderRadius: '4px', bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }
                }}
              />
            </MuiGrid>
          </MuiGrid>
        </Stack>
      </Paper>

      {/* ── Table Section ── */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flexGrow: 1,
          borderRadius: '4px',
          border: '1px solid #e2e8f0',
          bgcolor: '#fff',
          overflowY: 'auto',
          position: 'relative',
          boxShadow: '0 2px 16px rgba(0,0,0,0.05)'
        }}
      >
        <Table stickyHeader>
          <TableHead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableCell
                    key={header.id}
                    sx={{
                      background: 'linear-gradient(180deg,#f8fafc 0%,#f1f5f9 100%)',
                      color: '#475569',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      py: 1.8,
                      borderBottom: '2px solid #e2e8f0',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row, idx) => (
                <TableRow
                  key={row.original._id}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: idx % 2 === 0 ? '#fff' : '#fafbfc',
                    transition: 'background 0.15s, box-shadow 0.15s',
                    '&:hover': {
                      bgcolor: '#eff6ff',
                      boxShadow: 'inset 4px 0 0 #3b82f6'
                    },
                    '& td': { py: 1.4, borderBottom: '1px solid #f1f5f9' }
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id} sx={{ fontSize: '0.85rem' }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length || 10} align="center" sx={{ py: 12, border: 0 }}>
                  {loading ? (
                    <CircularProgress size={40} thickness={4} />
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{
                        width: 72, height: 72, borderRadius: '4px',
                        bgcolor: '#f1f5f9', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 2
                      }}>
                        <SearchIcon sx={{ fontSize: 36, color: '#94a3b8' }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} color="#334155">No leads found</Typography>
                      <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>Try adjusting filters or create a new lead</Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && table.getRowModel().rows.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} thickness={4} />
          </Box>
        )}
      </TableContainer>

      {/* ── Pagination Section ── */}
      <Paper
        elevation={0}
        sx={{
          p: '10px 16px',
          borderRadius: '4px',
          border: '1px solid #e2e8f0',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            Showing <b>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</b> to{' '}
            <b>{Math.min(page * pageSize, totalCount)}</b> of <b>{totalCount}</b> leads
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Rows per page:</Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              sx={{
                borderRadius: '4px',
                height: 32,
                fontSize: '0.8rem',
                bgcolor: '#f8fafc'
              }}
            >
              {[10, 20, 25, 30, 50, 100].map(sz => (
                <MenuItem key={sz} value={sz}>{sz}</MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, val) => setPage(val)}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
          size="small"
          sx={{
            '& .MuiPaginationItem-root': {
              borderRadius: '4px'
            }
          }}
        />
      </Paper>

      {/* ── User Assign Modal ── */}
      <Modal open={userModalOpen} onClose={() => setUserModalOpen(false)}>
        <Paper sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 420, borderRadius: '4px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
          overflow: 'hidden'
        }}>
          <Box sx={{ p: '20px 24px', background: 'linear-gradient(135deg,#1e40af,#3b82f6)', }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}>Assign Team Members</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', mt: 0.3 }}>Select who should handle this lead</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Select
              multiple fullWidth size="small"
              value={assignedUsers}
              onChange={(e) => setAssignedUsers(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {allUsers.filter(u => selected.includes(u.id)).map(u => (
                    <Chip key={u.id} label={u.fullName} size="small" sx={{ borderRadius: '4px', bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600 }} />
                  ))}
                </Box>
              )}
              sx={{ borderRadius: '4px' }}
            >
              {(() => {
                let options = allUsers;
                if (userRole === "Operation Manager") {
                  options = allUsers.filter(u => u.permission === "Operation");
                } else if (userRole === "Sales Manager" || userRole === "Sales Team Lead") {
                  options = allUsers.filter(u => ["Sales Executive", "Sales Team Lead"].includes(u.permission));
                } else if (userRole !== "Admin" && userRole !== "Admin-a") {
                  // If not admin and not one of the specific managers, give them a sensible default or everything
                  options = allUsers.filter(u => ["User", "Operation", "Sales Executive", "Sales Team Lead"].includes(u.permission));
                }
                return options;
              })().map(user => (
                <MenuItem key={user.id} value={user.id}>
                  <Checkbox checked={assignedUsers.includes(user.id)} size="small" />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 26, height: 26, fontSize: '0.7rem', bgcolor: '#3b82f6' }}>{user.fullName?.[0]}</Avatar>
                    <Typography variant="body2" fontWeight={500}>{user.fullName}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
            <Button fullWidth variant="contained" onClick={saveUserAssignments}
              sx={{ mt: 2.5, borderRadius: '4px', height: 44, fontWeight: 700, background: 'linear-gradient(135deg,#1e40af,#3b82f6)', boxShadow: '0 4px 16px rgba(59,130,246,0.35)' }}
            >
              Save Assignments
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Change Request View */}
      <Modal open={changeModalOpen} onClose={() => setChangeModalOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: '#f8fafc', boxShadow: 24, p: 3,
          width: 600, maxHeight: "85vh", overflowY: "auto", borderRadius: '4px'
        }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Review Change Requests</Typography>
          <Divider sx={{ mb: 2 }} />
          {selectedChangeRequests.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No pending requests</Typography>
          ) : (
            selectedChangeRequests.map(change => (
              <Paper key={change._id} sx={{ p: 2, mb: 2, borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                    <ChangeCircleIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>{change.user_id?.fullName || 'User'}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(change.date_time).toLocaleString()}</Typography>
                  </Box>
                  <Chip label={change.approved_status} size="small" color="primary" variant="outlined" sx={{ ml: 'auto' }} />
                </Stack>

                <Typography variant="body2" sx={{ bgcolor: '#ffffff', p: 1.5, borderRadius: '4px', border: '1px dashed #cbd5e1', mb: 2 }}>
                  {change.description}
                </Typography>

                {change.approved_status === "Pending" && checkPermission("operation", "verify") && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleChangeRequestAction(change._id, "Approved")}
                      sx={{ borderRadius: '4px', px: 3 }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        const reason = prompt("Enter rejection reason:");
                        if (reason) handleChangeRequestAction(change._id, "Rejected", reason);
                      }}
                      sx={{ borderRadius: '4px' }}
                    >
                      Reject
                    </Button>
                  </Stack>
                )}
              </Paper>
            ))
          )}
          <Button onClick={() => setChangeModalOpen(false)} fullWidth sx={{ mt: 1, color: 'text.secondary' }}>Close</Button>
        </Box>
      </Modal>

      {/* Rejected Reason View */}
      <Modal open={rejectedModalOpen} onClose={() => setRejectedModalOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: '#fff', p: 3, width: 500,
          borderRadius: '4px', boxShadow: 24, maxHeight: '80vh', overflowY: 'auto'
        }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>History of Rejections</Typography>
          <Divider sx={{ mb: 2 }} />
          {rejectedChanges.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" py={4}>No history found.</Typography>
          ) : (
            rejectedChanges.map(change => (
              <Paper key={change._id} sx={{ p: 2, mb: 2, bgcolor: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '4px' }}>
                <Typography variant="subtitle2" fontWeight={700}>{new Date(change.date_time).toLocaleDateString()}</Typography>
                <Typography variant="body2" sx={{ my: 1 }}>{change.description}</Typography>
                <Box sx={{ p: 1, bgcolor: '#ffffff', borderRadius: '4px', borderLeft: '3px solid #ef4444' }}>
                  <Typography variant="caption" color="error.main" fontWeight={700}>REJECTION REASON:</Typography>
                  <Typography variant="body2" color="error.main">{change.rejected_reason}</Typography>
                </Box>
              </Paper>
            ))
          )}
          <Button onClick={() => setRejectedModalOpen(false)} fullWidth color="inherit">Close</Button>
        </Box>
      </Modal>

      <SnackbarComponent />

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: '4px', p: 0, overflow: 'hidden', maxWidth: 420 } }}>
        <Box sx={{ p: '18px 24px', bgcolor: '#fef2f2', borderBottom: '1px solid #fee2e2' }}>
          <Typography sx={{ fontWeight: 800, color: '#dc2626', fontSize: '1.05rem' }}>Delete Lead</Typography>
        </Box>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This action is <b>permanent</b> and cannot be undone. The lead and all its data will be removed.
          </Typography>
          {queryToDelete && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff5f5', borderRadius: '4px', borderColor: '#fecaca' }}>
              <Typography variant="subtitle2" fontWeight={700}>{queryToDelete.guest_info?.guest_name}</Typography>
              <Typography variant="caption" color="text.secondary">ID: {queryToDelete._id}</Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px', gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: '4px', color: '#64748b', fontWeight: 600 }}>Keep Lead</Button>
          <Button color="error" variant="contained" onClick={handleDeleteQuery}
            sx={{ borderRadius: '4px', px: 3, fontWeight: 700, boxShadow: '0 4px 14px rgba(220,38,38,0.3)' }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Remarks Dialog ── */}
      <Dialog open={remarksModalOpen} onClose={() => setRemarksModalOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '4px', overflow: 'hidden' } }}
      >
        <Box sx={{ p: '18px 24px', background: 'linear-gradient(135deg,#9c27b0,#ba68c8)' }}>
          <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1.1rem' }}>Remarks</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', mt: 0.3 }}>
            Lead: {selectedQueryForRemarks?.guest_info?.guest_name || 'Unknown'}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 300, maxHeight: 500 }}>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, pr: 1 }}>
            {(!selectedQueryForRemarks?.remarks || selectedQueryForRemarks.remarks.length === 0) ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>No remarks yet.</Typography>
            ) : (
              selectedQueryForRemarks.remarks.map((remark, idx) => {
                const isAdmin = userRole === 'Admin' || userRole === 'Admin-a';
                const isOwner = String(remark.added_by) === String(userId);
                const remarkTime = new Date(remark.date).getTime();
                const hoursDifference = (Date.now() - remarkTime) / (1000 * 60 * 60);
                const canEditDelete = isAdmin || (isOwner && hoursDifference <= 3);

                return (
                  <Paper key={idx} sx={{ p: 1.5, bgcolor: '#f3e5f5', borderRadius: '4px', border: '1px solid #e1bee7' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#6a1b9a' }}>
                        {remark.added_by_name || 'User'}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {new Date(remark.date).toLocaleString()}
                        </Typography>
                        {canEditDelete && (
                          <>
                            <IconButton size="small" onClick={() => { setEditingRemarkId(remark._id); setEditRemarkText(remark.text); }} sx={{ p: 0.5 }}>
                              <EditIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteRemark(remark._id)} sx={{ p: 0.5 }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </Stack>
                    {editingRemarkId === remark._id ? (
                      <Stack direction="row" spacing={1} mt={1}>
                        <TextField 
                          size="small" fullWidth value={editRemarkText} 
                          onChange={(e) => setEditRemarkText(e.target.value)}
                        />
                        <Button size="small" variant="contained" color="primary" onClick={() => handleEditRemarkSave(remark._id)}>Save</Button>
                        <Button size="small" onClick={() => setEditingRemarkId(null)}>Cancel</Button>
                      </Stack>
                    ) : (
                      <Typography variant="body2">{remark.text}</Typography>
                    )}
                  </Paper>
                );
              })
            )}
          </Box>
          <Divider />
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type a new remark..."
              value={newRemarkText}
              onChange={(e) => setNewRemarkText(e.target.value)}
              multiline
              rows={2}
              InputProps={{ sx: { borderRadius: '4px' } }}
            />
            <Button
              variant="contained"
              onClick={handleAddRemark}
              disabled={remarkLoading || !newRemarkText.trim()}
              sx={{ borderRadius: '4px', px: 3, bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
            >
              {remarkLoading ? <CircularProgress size={20} color="inherit" /> : 'Add'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* ── Quick Edit Dialog ── */}
      <Dialog open={quickEditOpen} onClose={() => setQuickEditOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '4px', overflow: 'hidden' } }}
      >
        <Box sx={{ p: '16px 20px', background: 'linear-gradient(135deg,#1e40af,#3b82f6)' }}>
          <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1.05rem' }}>Quick Edit Lead</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', mt: 0.3 }}>Update contact details, email, and address</Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5, pb: 1.5 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth label="Lead Name" size="small"
              value={editedRowData.guest_name || ""}
              onChange={(e) => setEditedRowData(prev => ({ ...prev, guest_name: e.target.value }))}
              InputProps={{ sx: { borderRadius: '4px' } }}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                sx={{ width: 100 }} label="Code" size="small"
                value={editedRowData.guest_country_code || "+91"}
                onChange={(e) => setEditedRowData(prev => ({ ...prev, guest_country_code: e.target.value }))}
                InputProps={{ sx: { borderRadius: '4px' } }}
              />
              <TextField
                fullWidth label="Phone" size="small"
                value={editedRowData.guest_phone || ""}
                onChange={(e) => setEditedRowData(prev => ({ ...prev, guest_phone: e.target.value }))}
                InputProps={{ sx: { borderRadius: '4px' } }}
              />
            </Stack>
            <TextField
              fullWidth label="Email Address" size="small" type="email"
              value={editedRowData.guest_email || ""}
              onChange={(e) => setEditedRowData(prev => ({ ...prev, guest_email: e.target.value }))}
              InputProps={{ sx: { borderRadius: '4px' } }}
            />
            <TextField
              fullWidth label="Address / Location" size="small" multiline rows={2}
              value={editedRowData.guest_address || ""}
              onChange={(e) => setEditedRowData(prev => ({ ...prev, guest_address: e.target.value }))}
              InputProps={{ sx: { borderRadius: '4px' } }}
            />
            <TextField
              fullWidth select label="Lead Stage" size="small"
              value={editedRowData.lead_stage || ""}
              onChange={(e) => setEditedRowData(prev => ({ ...prev, lead_stage: e.target.value }))}
              InputProps={{ sx: { borderRadius: '4px' } }}
            >
              {["Confirm", "Cancel", "FollowUp", "Postponed", "Higher Priority"].map(s => (
                <MenuItem key={s} value={s}>
                  <ModernStatusChip status={s} />
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth label="Total Cost" type="number" size="small"
                disabled={isQuickEditLocked}
                value={(editedRowData.cost === 0 || editedRowData.cost === '0') ? 0 : (editedRowData.cost || "")}
                onChange={(e) => setEditedRowData(prev => ({ ...prev, cost: e.target.value }))}
                helperText={isQuickEditLocked ? "Locked after advance" : ""}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment>, sx: { borderRadius: '4px' } }}
              />
              <TextField
                fullWidth label="Advance" type="number" size="small"
                disabled={isQuickEditLocked}
                value={(editedRowData.advance === 0 || editedRowData.advance === '0') ? 0 : (editedRowData.advance || "")}
                onChange={(e) => setEditedRowData(prev => ({ ...prev, advance: e.target.value }))}
                helperText={isQuickEditLocked ? "Locked after advance" : ""}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment>, sx: { borderRadius: '4px' } }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: '12px 20px 16px', gap: 1 }}>
          <Button onClick={() => setQuickEditOpen(false)} sx={{ borderRadius: '4px', color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={() => handleSaveEdit(selectedQuickEditId, editedRowData)}
            sx={{ borderRadius: '4px', px: 3, fontWeight: 700, background: 'linear-gradient(135deg,#1e40af,#3b82f6)', boxShadow: '0 2px 10px rgba(59,130,246,0.3)' }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentScheduleModal 
        open={scheduleModalOpen}
        onClose={() => { setScheduleModalOpen(false); setSelectedQueryForSchedule(null); }}
        queryData={selectedQueryForSchedule}
      />
    </Container>
  );
};

export default Query;
